import React, { useState } from "react";
import { Button as ReactstrapButton } from "reactstrap";
import { Button, Tag, Space, Badge } from "antd";
import type { TableColumnsType } from "antd";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkBoqAside from "./workBoqAside";

interface WorkBoqData {
  id: string;
  boqTitle: string;
  siteName: string;
  workLevel: string;
  contractor: string;
  totalLines: number;
  status: "Draft" | "Pending Approval" | "Approved" | "Rejected";
  createdAt: string;
  createdBy: string;
}

const WorkBoqList: React.FC = () => {
  const [sm, setSm] = useState(false);

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      "Draft": "default",
      "Pending Approval": "processing",
      "Approved": "success",
      "Rejected": "error",
    };
    return statusColors[status] || "default";
  };

  const handleEdit = (record: WorkBoqData) => {
    // Navigate to edit mode
    window.location.href = `/work-boq-creation?edit=${record.id}`;
  };

  const handleView = (record: WorkBoqData) => {
    // Navigate to view details
    window.location.href = `/work-boq-view/${record.id}`;
  };

  const handleDelete = (record: WorkBoqData) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // API call would go here to delete the record
        Swal.fire("Deleted!", "Work BOQ has been deleted.", "success");
      }
    });
  };

  const handleSubmitForApproval = (record: WorkBoqData) => {
    Swal.fire({
      title: "Submit for Approval",
      text: "Are you sure you want to submit this Work BOQ for approval?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, submit it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // API call would go here to update status
        Swal.fire("Submitted!", "Work BOQ has been submitted for approval.", "success");
      }
    });
  };

  const columns: TableColumnsType<WorkBoqData> = [
    {
      title: "BOQ ID",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "BOQ Title",
      dataIndex: "boqTitle",
      key: "boqTitle",
      width: 250,
      render: (text, record) => (
        <div>
          <Link to={`/work-boq-view/${record.id}`} className="fw-bold text-primary">
            {text}
          </Link>
          <div className="text-muted small mt-1">{record.siteName}</div>
        </div>
      ),
    },
    {
      title: "Work Level",
      dataIndex: "workLevel",
      key: "workLevel",
      width: 120,
      render: (text) => (
        <Tag color="blue">{text}</Tag>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 180,
    },
    {
      title: "Lines",
      dataIndex: "totalLines",
      key: "totalLines",
      width: 80,
      align: "center",
      render: (text) => (
        <Badge color="info">{text}</Badge>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (text, record) => (
        <div>
          <div>{dayjs(text).format("DD-MMM-YYYY")}</div>
          <small className="text-muted">by {record.createdBy}</small>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 350,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<Icon name="eye" />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          
          {record.status === "Draft" && (
            <>
              <Button
                type="link"
                size="small"
                icon={<Icon name="edit" />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>
              <Button
                type="link"
                size="small"
                icon={<Icon name="send" />}
                onClick={() => handleSubmitForApproval(record)}
              >
                Submit
              </Button>
            </>
          )}
          
          {(record.status === "Draft" || record.status === "Rejected") && (
            <Button
              type="link"
              size="small"
              danger
              icon={<Icon name="trash" />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-boq-list"
      title="Work BOQ Management"
      columns={columns}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkBoqAside updateSm={setSm} sm={sm} />
        </div>
      }
      // topRightJSX={
      //   <Link to="/work-boq-creation">
      //     <ReactstrapButton color="primary">
      //       <Icon name="plus" />
      //       Create Work BOQ
      //     </ReactstrapButton>
      //   </Link>
      // }
      extra={{
        tableScrollX: 1200,
      }}
    />
  );
};

export default WorkBoqList;